import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./love-match-report-style.css";
import StatusMap from "../../../../constant/menulist";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";
import SiteFooter from "../../../custom/site-footer/site-footer";
import useIsMobile from "../../../../hooks/useIsMobile";
import { shareOrCopy } from "../../../../utils/share";

//分享 / 官方 LINE（footer 用）
const SHARE_URL = "https://yuanchen.leinotek.com/";
const LINE_URL = "https://lin.ee/v05Pe75";
const handleShare = (e) => {
  e.preventDefault();
  shareOrCopy({
    title: "元辰雲閣",
    text: "元辰雲閣｜真愛合盤・看你們合不合",
    url: SHARE_URL,
  }).then((r) => {
    if (r === "copied") window.alert("已複製連結，貼上即可分享");
  });
};

//天干 → 五行卡片圖（檔名按天干順序：甲乙 = wood-1/2、丙丁 = fire-3/4、戊己 = earth-5/6、庚辛 = metal-7/8、壬癸 = water-9/10）
const GAN_CARD_IMG = {
  甲: "bg-card-wood-1.png",
  乙: "bg-card-wood-2.png",
  丙: "bg-card-fire-3.png",
  丁: "bg-card-fire-4.png",
  戊: "bg-card-earth-5.png",
  己: "bg-card-earth-6.png",
  庚: "bg-card-metal-7.png",
  辛: "bg-card-metal-8.png",
  壬: "bg-card-water-9.png",
  癸: "bg-card-water-10.png",
};

const LoveMatchReportComponent = ({
  data: propData,
  setShowServiceMenu,
  setShowPersonalMenu,
}) => {
  const isMobile = useIsMobile();

  const reelRef = useRef(null);
  const outerPaperRef = useRef(null);
  const topHolderRef = useRef(null);
  const bottomHolderRef = useRef(null);
  const mobileReelRef = useRef(null);
  const mobileOuterPaperRef = useRef(null);
  const mobileTopHolderRef = useRef(null);
  const mobileBottomHolderRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  //資料來源：propData（後台「查看產品」直接帶入）優先，其次 navigate state
  const data = propData ?? location.state?.data;

  const chartData = data?.chartData ?? [];
  const scoreTableData = data?.scoreTableData ?? [];

  //沒有報告資料（直接進網址 / 重新整理）就導回入口頁
  useEffect(() => {
    if (!data) navigate("/love-enter", { replace: true });
  }, [data, navigate]);

  // 網頁版卷軸開場動畫
  useEffect(() => {
    const reel = reelRef.current;
    const paper = outerPaperRef.current;
    const topHolder = topHolderRef.current;
    const holder = bottomHolderRef.current;
    if (!reel || !paper || !topHolder || !holder) return;

    let rafId;

    const startAnimation = () => {
      holder.style.transform = "";
      const initialY =
        topHolder.getBoundingClientRect().top -
        holder.getBoundingClientRect().top;
      holder.style.transform = `translateY(${initialY}px)`;
      holder.style.opacity = "1";

      const DURATION = 3500;
      const easeInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
      let startTime = null;

      const animate = (ts) => {
        if (!startTime) startTime = ts;
        const t = Math.min((ts - startTime) / DURATION, 1);
        const p = easeInOut(t);
        holder.style.transform = `translateY(${initialY * (1 - p)}px)`;
        paper.style.clipPath = `inset(0 0 ${(1 - p) * 100}% 0)`;
        if (t < 1) rafId = requestAnimationFrame(animate);
      };

      rafId = requestAnimationFrame(animate);
    };

    const checkAndStart = () => {
      if (topHolder.complete && holder.complete) startAnimation();
    };

    topHolder.addEventListener("load", checkAndStart);
    holder.addEventListener("load", checkAndStart);
    checkAndStart();

    return () => {
      topHolder.removeEventListener("load", checkAndStart);
      holder.removeEventListener("load", checkAndStart);
      cancelAnimationFrame(rafId);
    };
  }, []);

  // 手機版卷軸開場動畫
  useEffect(() => {
    const reel = mobileReelRef.current;
    const paper = mobileOuterPaperRef.current;
    const topHolder = mobileTopHolderRef.current;
    const holder = mobileBottomHolderRef.current;
    if (!reel || !paper || !topHolder || !holder) return;

    let rafId;

    const startAnimation = () => {
      holder.style.transform = "";
      const initialY =
        topHolder.getBoundingClientRect().top -
        holder.getBoundingClientRect().top;
      holder.style.transform = `translateY(${initialY}px)`;
      holder.style.opacity = "1";

      const DURATION = 3500;
      const easeInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
      let startTime = null;

      const animate = (ts) => {
        if (!startTime) startTime = ts;
        const t = Math.min((ts - startTime) / DURATION, 1);
        const p = easeInOut(t);
        holder.style.transform = `translateY(${initialY * (1 - p)}px)`;
        paper.style.clipPath = `inset(0 0 ${(1 - p) * 100}% 0)`;
        if (t < 1) rafId = requestAnimationFrame(animate);
      };

      rafId = requestAnimationFrame(animate);
    };

    const checkAndStart = () => {
      if (topHolder.complete && holder.complete) startAnimation();
    };

    topHolder.addEventListener("load", checkAndStart);
    holder.addEventListener("load", checkAndStart);
    checkAndStart();

    return () => {
      topHolder.removeEventListener("load", checkAndStart);
      holder.removeEventListener("load", checkAndStart);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const renderScoreBlocks = (score) => (
    <div className="score-block-group">
      <div className="score-blocks">
        {Array.from({ length: score }).map((_, i) => (
          <span key={i} className="score-block"></span>
        ))}
      </div>
    </div>
  );

  const matchKeys = [
    { title: "價值觀契合", key: "value" },
    { title: "能量互動", key: "interactive" },
    { title: "角色分工", key: "role" },
    { title: "溝通模式", key: "communicate" },
    { title: "物質觀與務實性", key: "physical" },
  ];

  //對方的代名詞：男 → 「他」、女 → 「她」（報告各處稱呼對方一律用這個）
  const objectPronoun = data?.object?.gender === "男" ? "他" : "她";

  //主力欄位顯示：對方贏 → 用對方代名詞（他 / 她）；你贏 → 「你」；平手 → 「平手」
  const dominantLabel = (dominant) =>
    dominant === "對方" ? objectPronoun : dominant;

  if (!data) return null;

  return (
    <div className="love-match-report-page">
      {/*網頁版*/}
      {!isMobile && (
        <>
          <div
            className="web-main"
            onMouseEnter={() => {
              setShowServiceMenu(false);
              setShowPersonalMenu(false);
            }}
          >
            {data.betweenStatus == "0" ? (
              <div className="reel-main" ref={reelRef}>
                <img
                  className="top-holder"
                  ref={topHolderRef}
                  src="/images/bg-top-reel.png"
                />
                <div className="outer-paper" ref={outerPaperRef}>
                  <div className="inner-paper">
                    <div className="status">{`狀態：${StatusMap.loveStatusMap[data.betweenStatus]}`}</div>
                    <div className="between-info">
                      <div className="user">
                        <span>{data.user.name}</span>
                        <span>{`${data.user.gender}性`}</span>
                        <span>{data.user.birth}</span>
                      </div>
                      <img
                        className="love-holder"
                        src="/images/ic-love-report.png"
                      />
                      <div className="user">
                        <span>{data.object.name}</span>
                        <span>{`${data.object.gender}性`}</span>
                        <span>{data.object.birth}</span>
                      </div>
                    </div>
                    <div className="reel-title">
                      <img src="/images/bg-love-top-title.png" />
                      <span className="label">真愛合盤</span>
                      <span className="title">{data.slogan}</span>
                      <img src="/images/bg-love-bottom-title.png" />
                    </div>
                    {data.selfIdealType && (
                      <>
                        <span className="item-label">你可能喜歡的類型</span>
                        <div className="item-block">
                          <span className="item-title">{` ● 最愛類型（${data.selfIdealType.ideal?.label}）：`}</span>
                          <span className="item-content">
                            {data.selfIdealType.ideal?.des}
                          </span>
                          <span className="item-title">{` ● 內心潛意識（${data.selfIdealType.subconscious?.label}）：`}</span>
                          <span className="item-content">
                            {data.selfIdealType.subconscious?.des}
                          </span>
                        </div>
                      </>
                    )}
                    <span className="item-label">對方個性 (外在 × 內在)</span>
                    <div className="item-block">
                      <span className="item-title">
                        {`對象：${data.object.birth} (${data.object.dayMaster}日主，生於${data.object.monthZhi}月，${data.object.gender})`}
                      </span>
                      <span className="item-title">{` ● 外在氣場（${data.objectPersonality.outer.label}）：`}</span>
                      <span className="item-content">
                        {data.objectPersonality.outer.des}
                      </span>
                      <span className="item-title">{` ● 內在靈魂 (${data.objectPersonality.inner.label}）：`}</span>
                      <span className="item-content">
                        {data.objectPersonality.inner.des}
                      </span>
                      <img
                        className="attr-card"
                        src={`/images/${GAN_CARD_IMG[data.object.dayMaster?.[0]] ?? "bg-card-wood-1.png"}`}
                      />
                    </div>
                    <span className="item-label">{`${objectPronoun}對感情的態度`}</span>
                    <div className="item-block">
                      <span className="item-title">{`● 關鍵詞：${data.objectAttribute.keyword}`}</span>
                      <span className="item-title">{`● ${objectPronoun}的愛情觀：`}</span>
                      <span className="item-content">
                        {data.objectAttribute.concept}
                      </span>
                      <span className="item-title">● 實際付出：</span>
                      <span className="item-content">
                        {data.objectAttribute.pay}
                      </span>
                    </div>
                    <span className="item-label">可能相互吸引的點</span>
                    <div className="item-block">
                      <span className="item-title">{`● ${data.mutualAttraction.summary} :`}</span>
                      <span className="item-title">{`● 你為什麼迷戀${objectPronoun}：`}</span>
                      <span className="item-content">
                        {data.mutualAttraction.loveReason}
                      </span>
                      <span className="item-title">{`● ${objectPronoun}為什麼離不開你：`}</span>
                      <span className="item-content">
                        {data.mutualAttraction.nonLeaveReason}
                      </span>
                    </div>
                    <span className="item-label">
                      你們的速配程度：<b>{data.matchAttribute.score}</b> 分
                    </span>
                    <div className="match-block">
                      {matchKeys.map(({ title, key }) => (
                        <div className="item-match" key={key}>
                          <div className="match-rating">
                            <span className="match-title">{title}</span>
                            <div className="stars">
                              {Array.from({
                                length: data.matchAttribute[key].stars,
                              }).map((_, i) => (
                                <img key={i} src="/images/ic-yellow-star.png" />
                              ))}
                            </div>
                          </div>
                          <span className="item-content">
                            {data.matchAttribute[key].des}
                          </span>
                        </div>
                      ))}
                    </div>
                    <span className="item-label">交往前相處指南</span>
                    <div className="item-block">
                      <span className="item-title">
                        {data.beforeGuide.summary}
                      </span>
                      {data.beforeGuide.strategys.map((s, i) => (
                        <div className="strateg-block" key={i}>
                          <span className="item-title">{`● ${s.label}：${s.advice}`}</span>
                          <span className="item-content">{s.explain}</span>
                        </div>
                      ))}
                    </div>
                    <span className="item-label">若交往後的相處與可能衝突</span>
                    <div className="item-block">
                      <span className="item-title">● 日常相處：</span>
                      <span className="item-content">
                        {data.afterGuide.together.daily}
                      </span>
                      <span className="item-title">● 相處氛圍：</span>
                      <span className="item-content">
                        {data.afterGuide.together.atmosphere}
                      </span>
                      <span className="item-title">● 可能衝突：</span>
                      <span className="item-title-2">
                        {data.afterGuide.conflict.summary}
                      </span>
                      <span className="item-title">● 衝突場景：</span>
                      <span className="item-content">
                        {data.afterGuide.conflict.situation}
                      </span>
                      <span className="item-title">● 你的感受：</span>
                      <span className="item-content">
                        {data.afterGuide.conflict.yourFeeling}
                      </span>
                      <span className="item-title">{`● ${objectPronoun}的感受：`}</span>
                      <span className="item-content">
                        {data.afterGuide.conflict.objectFeeling}
                      </span>
                      <span className="item-title">● 化解方式：</span>
                      <span className="item-content">
                        {data.afterGuide.conflict.solveMethod}
                      </span>
                    </div>
                    <span className="item-label">戀愛體質雷達圖</span>
                    <RadarChart
                      width={400}
                      height={400}
                      data={chartData}
                      outerRadius="65%"
                      style={{ pointerEvents: "none" }}
                    >
                      <PolarGrid
                        gridType="polygon"
                        stroke="#c6c6c6"
                        strokeDasharray="8 4"
                      />
                      <PolarAngleAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fill: "#615557",
                          fontSize: 15,
                          fontWeight: 500,
                        }}
                      />
                      <PolarRadiusAxis
                        domain={[0, 5]}
                        ticks={[1, 2, 3, 4, 5]}
                        tick={false}
                        axisLine={false}
                      />
                      <Radar
                        dataKey="full"
                        stroke="#707070"
                        strokeWidth={3}
                        fill="none"
                        dot={false}
                        isAnimationActive={false}
                      />
                      <Radar
                        name="對方"
                        dataKey="partner"
                        fill="#ff7070"
                        fillOpacity={0.5}
                      />
                      <Radar
                        name="你"
                        dataKey="you"
                        fill="#85d6ff"
                        fillOpacity={0.5}
                      />
                    </RadarChart>
                    <div className="compare-grid">
                      <div className="cell header">維度</div>
                      <div className="cell header">
                        <span>你的</span>
                        <span>指數</span>
                      </div>
                      <div className="cell header">
                        <span>{`${objectPronoun}的`}</span>
                        <span>指數</span>
                      </div>
                      <div className="cell header">
                        <span>誰是</span>
                        <span>主力</span>
                      </div>
                      <div className="cell header">解析</div>
                      {scoreTableData.map((item, i) => (
                        <>
                          <div key={i} className="cell dimension">
                            <span>{item.dimension.slice(0, 2)}</span>
                            <span>{item.dimension.slice(2)}</span>
                          </div>
                          <div className="cell">
                            {renderScoreBlocks(item.youScore)}
                          </div>
                          <div className="cell">
                            {renderScoreBlocks(item.partnerScore)}
                          </div>
                          <div className="cell dominant">
                            {item.dominant !== "平手" && (
                              <span className="crown">👑</span>
                            )}
                            <span>{dominantLabel(item.dominant)}</span>
                          </div>
                          <div className="cell analysis">{item.analysis}</div>
                        </>
                      ))}
                    </div>
                    <span className="item-label">總結</span>
                    <div className="item-block">
                      <span className="item-title">{data.summary.simple}</span>
                      <span className="item-content">
                        {data.summary.detail}
                      </span>
                    </div>
                    <div className="memo">
                      <div className="links">
                        <a href={SHARE_URL} onClick={handleShare}>👉 點擊分享 </a>
                        <span>|</span>
                        <a href={LINE_URL} target="_blank" rel="noopener noreferrer">👉 官方LINE客服 </a>
                        <span>|</span>
                        <Link to={`/consult?order=${data.orderID || ""}`}>
                          👉 專業諮詢{" "}
                        </Link>
                      </div>
                      <span className="product-id">{`產品編號 ${data.productID}`}</span>
                      <span className="end">
                        本分析由元辰雲閣命理技術團隊提供，為你在交往前解析關係發展與相處模式。
                      </span>
                    </div>
                  </div>
                </div>
                <img
                  className="bottom-holder"
                  ref={bottomHolderRef}
                  src="/images/bg-bottom-reel.png"
                  style={{ opacity: 0 }}
                />
              </div>
            ) : (
              <div className="reel-main" ref={reelRef}>
                <img
                  className="top-holder"
                  ref={topHolderRef}
                  src="/images/bg-top-reel.png"
                />
                <div className="outer-paper" ref={outerPaperRef}>
                  <div className="inner-paper">
                    <div className="status">{`狀態：${StatusMap.loveStatusMap[data.betweenStatus]}`}</div>
                    <div className="between-info">
                      <div className="user">
                        <span>{data.user.name}</span>
                        <span>{`${data.user.gender}性`}</span>
                        <span>{data.user.birth}</span>
                      </div>
                      <img
                        className="love-holder"
                        src="/images/ic-love-report.png"
                      />
                      <div className="user">
                        <span>{data.object.name}</span>
                        <span>{`${data.object.gender}性`}</span>
                        <span>{data.object.birth}</span>
                      </div>
                    </div>
                    <div className="reel-title">
                      <img src="/images/bg-love-top-title.png" />
                      <span className="label">真愛合盤</span>
                      <span className="title">{data.slogan}</span>
                      <img src="/images/bg-love-bottom-title.png" />
                    </div>
                    <span className="item-label">{`個性分析：${data.bwnPersonality.you.label} vs. ${data.bwnPersonality.object.label}`}</span>
                    <div className="item-block">
                      <span className="item-title">{` ● 你：`}</span>
                      <span className="item-content">
                        {data.bwnPersonality.you.des}
                      </span>
                      <span className="item-title">{` ● 對方：`}</span>
                      <span className="item-content">
                        {data.bwnPersonality.object.des}
                      </span>
                    </div>
                    <span className="item-label">溝通模式與互動方式</span>
                    <div className="item-block">
                      <span className="item-title">● 主要溝通模式：</span>
                      <span className="item-content">
                        {data.communicateActive.communicate}
                      </span>
                      <span className="item-title">● 日常互動模式：</span>
                      <span className="item-content">
                        {data.communicateActive.active}
                      </span>
                      <span className="item-title">● 建議：</span>
                      <span className="item-content">
                        {data.communicateActive.advice}
                      </span>
                    </div>
                    <span className="item-label">吵架點分析</span>
                    <div className="item-block">
                      <span className="item-title">{data.quarrel.simple}</span>
                      <span className="item-title">● 你的引爆點：</span>
                      <span className="item-content">
                        {data.quarrel.youOpinion}
                      </span>
                      <span className="item-title">● 對方的引爆點：</span>
                      <span className="item-content">
                        {data.quarrel.objectOpinion}
                      </span>
                      <span className="item-title">● 吵架模式：</span>
                      <span className="item-content">
                        {data.quarrel.situation}
                      </span>
                      <span className="item-title">● 建議：</span>
                      <span className="item-content">
                        {data.quarrel.advice}
                      </span>
                    </div>
                    <span className="item-label">彼此的感情真實需求</span>
                    <div className="demand-grid">
                      <div className="cell header">誰的需求</div>
                      <div className="cell header">感情真實需求</div>
                      <div className="cell role">你</div>
                      <div className="cell content">
                        {data.demandData.youDemand}
                      </div>
                      <div className="cell role">對方</div>
                      <div className="cell content">
                        {data.demandData.objectDemand}
                      </div>
                    </div>
                    <span className="item-label">如何穩定走長期</span>
                    <div className="item-block">
                      <span className="item-title">{`● 長期穩定度評估：${data.stableStrategy.grade}`}</span>
                      {data.stableStrategy.strategys.map((s, i) => (
                        <div className="strateg-block" key={i}>
                          <span className="item-title">{`● ${s.label} :`}</span>
                          <span className="item-content">{s.strategy}</span>
                        </div>
                      ))}
                    </div>
                    <span className="item-label">
                      戀愛合拍指數：<b>{data.matchAttribute.score}</b> 分
                    </span>
                    <div className="match-block">
                      {matchKeys.map(({ title, key }) => (
                        <div className="item-match" key={key}>
                          <div className="match-rating">
                            <span className="match-title">{title}</span>
                            <div className="stars">
                              {Array.from({
                                length: data.matchAttribute[key].stars,
                              }).map((_, i) => (
                                <img key={i} src="/images/ic-yellow-star.png" />
                              ))}
                            </div>
                          </div>
                          <span className="item-content">
                            {data.matchAttribute[key].des}
                          </span>
                        </div>
                      ))}
                    </div>
                    <span className="item-label">戀愛體質雷達圖</span>
                    <RadarChart
                      width={400}
                      height={400}
                      data={chartData}
                      outerRadius="65%"
                      style={{ pointerEvents: "none" }}
                    >
                      <PolarGrid
                        gridType="polygon"
                        stroke="#c6c6c6"
                        strokeDasharray="8 4"
                      />
                      <PolarAngleAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fill: "#615557",
                          fontSize: 15,
                          fontWeight: 500,
                        }}
                      />
                      <PolarRadiusAxis
                        domain={[0, 5]}
                        ticks={[1, 2, 3, 4, 5]}
                        tick={false}
                        axisLine={false}
                      />
                      <Radar
                        dataKey="full"
                        stroke="#707070"
                        strokeWidth={3}
                        fill="none"
                        dot={false}
                        isAnimationActive={false}
                      />

                      <Radar
                        name="對方"
                        dataKey="partner"
                        fill="#ff7070"
                        fillOpacity={0.5}
                      />

                      <Radar
                        name="你"
                        dataKey="you"
                        fill="#85d6ff"
                        fillOpacity={0.5}
                      />
                    </RadarChart>
                    <div className="compare-grid">
                      <div className="cell header">維度</div>
                      <div className="cell header">
                        <span>你的</span>
                        <span>指數</span>
                      </div>
                      <div className="cell header">
                        <span>{`${objectPronoun}的`}</span>
                        <span>指數</span>
                      </div>
                      <div className="cell header">
                        <span>誰是</span>
                        <span>主力</span>
                      </div>
                      <div className="cell header">解析</div>
                      {scoreTableData.map((item, i) => (
                        <>
                          <div key={i} className="cell dimension">
                            <span>{item.dimension.slice(0, 2)}</span>
                            <span>{item.dimension.slice(2)}</span>
                          </div>
                          <div className="cell">
                            {renderScoreBlocks(item.youScore)}
                          </div>
                          <div className="cell">
                            {renderScoreBlocks(item.partnerScore)}
                          </div>
                          <div className="cell dominant">
                            {item.dominant !== "平手" && (
                              <span className="crown">👑</span>
                            )}
                            <span>{dominantLabel(item.dominant)}</span>
                          </div>
                          <div className="cell analysis">{item.analysis}</div>
                        </>
                      ))}
                    </div>
                    <span className="item-label">總結</span>
                    <div className="item-block">
                      <span className="item-title">{data.summary.simple}</span>
                      <span className="item-content">
                        {data.summary.detail}
                      </span>
                    </div>
                    <div className="memo">
                      <div className="links">
                        <a href={SHARE_URL} onClick={handleShare}>👉 點擊分享 </a>
                        <span>|</span>
                        <a href={LINE_URL} target="_blank" rel="noopener noreferrer">👉 官方LINE客服 </a>
                        <span>|</span>
                        <Link to={`/consult?order=${data.orderID || ""}`}>
                          👉 專業諮詢{" "}
                        </Link>
                      </div>
                      <span className="product-id">{`產品編號 ${data.productID}`}</span>
                      <span className="end">{`本分析由元辰雲閣命理技術團隊提供，${data.betweenStatus == "0" ? "為你在交往前解析關係發展與相處模式。" : "為你解析交往中的相處模式與需求。"}`}</span>
                    </div>
                  </div>
                </div>
                <img
                  className="bottom-holder"
                  ref={bottomHolderRef}
                  src="/images/bg-bottom-reel.png"
                  style={{ opacity: 0 }}
                />
              </div>
            )}

            <SiteFooter />
          </div>
        </>
      )}

      {/*手機版*/}
      {isMobile && (
        <div className="mobile-main">
          {data.betweenStatus == "0" ? (
            <div className="reel-main" ref={mobileReelRef}>
              <img
                className="top-holder"
                ref={mobileTopHolderRef}
                src="/images/bg-top-reel.png"
              />
              <div className="outer-paper" ref={mobileOuterPaperRef}>
                <div className="inner-paper">
                  <div className="status">{`狀態：${StatusMap.loveStatusMap[data.betweenStatus]}`}</div>
                  <div className="between-info">
                    <div className="user">
                      <span>{data.user.name}</span>
                      <span>{`${data.user.gender}性`}</span>
                      <span>{data.user.birth}</span>
                    </div>
                    <img
                      className="love-holder"
                      src="/images/ic-love-report.png"
                    />
                    <div className="user">
                      <span>{data.object.name}</span>
                      <span>{`${data.object.gender}性`}</span>
                      <span>{data.object.birth}</span>
                    </div>
                  </div>
                  <div className="reel-title">
                    <img src="/images/bg-love-top-title.png" />
                    <span className="label">真愛合盤</span>
                    <span className="title">{data.slogan}</span>
                    <img src="/images/bg-love-bottom-title.png" />
                  </div>
                  {data.selfIdealType && (
                    <>
                      <span className="item-label">我可能喜歡的類型</span>
                      <div className="item-block">
                        <span className="item-title">{` ● 最愛類型（${data.selfIdealType.ideal?.label}）：`}</span>
                        <span className="item-content">
                          {data.selfIdealType.ideal?.des}
                        </span>
                        <span className="item-title">{` ● 內心潛意識（${data.selfIdealType.subconscious?.label}）：`}</span>
                        <span className="item-content">
                          {data.selfIdealType.subconscious?.des}
                        </span>
                      </div>
                    </>
                  )}
                  <span className="item-label">對方個性 (外在 × 內在)</span>
                  <div className="item-block">
                    <span className="item-title">
                      {`對象：${data.object.birth} (${data.object.dayMaster}日主，生於${data.object.monthZhi}月，${data.object.gender})`}
                    </span>
                    <span className="item-title">{` ● 外在氣場（${data.objectPersonality.outer.label}）：`}</span>
                    <span className="item-content">
                      {data.objectPersonality.outer.des}
                    </span>
                    <span className="item-title">{` ● 內在靈魂 (${data.objectPersonality.inner.label}）：`}</span>
                    <span className="item-content">
                      {data.objectPersonality.inner.des}
                    </span>
                    <img
                      className="attr-card"
                      src={`/images/${GAN_CARD_IMG[data.object.dayMaster?.[0]] ?? "bg-card-wood-1.png"}`}
                    />
                  </div>
                  <span className="item-label">{`${objectPronoun}對感情的態度`}</span>
                  <div className="item-block">
                    <span className="item-title">{`● 關鍵詞：${data.objectAttribute.keyword}`}</span>
                    <span className="item-title">{`● ${objectPronoun}的愛情觀：`}</span>
                    <span className="item-content">
                      {data.objectAttribute.concept}
                    </span>
                    <span className="item-title">● 實際付出：</span>
                    <span className="item-content">
                      {data.objectAttribute.pay}
                    </span>
                  </div>
                  <span className="item-label">可能相互吸引的點</span>
                  <div className="item-block">
                    <span className="item-title">{`● ${data.mutualAttraction.summary} :`}</span>
                    <span className="item-title">{`● 你為什麼迷戀${objectPronoun}：`}</span>
                    <span className="item-content">
                      {data.mutualAttraction.loveReason}
                    </span>
                    <span className="item-title">{`● ${objectPronoun}為什麼離不開你：`}</span>
                    <span className="item-content">
                      {data.mutualAttraction.nonLeaveReason}
                    </span>
                  </div>
                  <span className="item-label">
                    你們的速配程度：<b>{data.matchAttribute.score}</b> 分
                  </span>
                  <div className="match-block">
                    {matchKeys.map(({ title, key }) => (
                      <div className="item-match" key={key}>
                        <div className="match-rating">
                          <span className="match-title">{title}</span>
                          <div className="stars">
                            {Array.from({
                              length: data.matchAttribute[key].stars,
                            }).map((_, i) => (
                              <img key={i} src="/images/ic-yellow-star.png" />
                            ))}
                          </div>
                        </div>
                        <span className="item-content">
                          {data.matchAttribute[key].des}
                        </span>
                      </div>
                    ))}
                  </div>
                  <span className="item-label">交往前相處指南</span>
                  <div className="item-block">
                    <span className="item-title">
                      {data.beforeGuide.summary}
                    </span>
                    {data.beforeGuide.strategys.map((s, i) => (
                      <div className="strateg-block" key={i}>
                        <span className="item-title">{`● ${s.label}：${s.advice}`}</span>
                        <span className="item-content">{s.explain}</span>
                      </div>
                    ))}
                  </div>
                  <span className="item-label">若交往後的相處與可能衝突</span>
                  <div className="item-block">
                    <span className="item-title">● 日常相處：</span>
                    <span className="item-content">
                      {data.afterGuide.together.daily}
                    </span>
                    <span className="item-title">● 相處氛圍：</span>
                    <span className="item-content">
                      {data.afterGuide.together.atmosphere}
                    </span>
                    <span className="item-title">● 可能衝突：</span>
                    <span className="item-title">
                      {data.afterGuide.conflict.summary}
                    </span>
                    <span className="item-title">● 衝突場景：</span>
                    <span className="item-content">
                      {data.afterGuide.conflict.situation}
                    </span>
                    <span className="item-title">● 你的感受：</span>
                    <span className="item-content">
                      {data.afterGuide.conflict.yourFeeling}
                    </span>
                    <span className="item-title">{`● ${objectPronoun}的感受：`}</span>
                    <span className="item-content">
                      {data.afterGuide.conflict.objectFeeling}
                    </span>
                    <span className="item-title">● 化解方式：</span>
                    <span className="item-content">
                      {data.afterGuide.conflict.solveMethod}
                    </span>
                  </div>
                  <span className="item-label">戀愛體質雷達圖</span>
                  <RadarChart
                    width={300}
                    height={300}
                    data={chartData}
                    outerRadius="65%"
                    style={{ pointerEvents: "none" }}
                  >
                    <PolarGrid
                      gridType="polygon"
                      stroke="#c6c6c6"
                      strokeDasharray="8 4"
                    />
                    <PolarAngleAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#615557", fontSize: 11, fontWeight: 500 }}
                    />
                    <PolarRadiusAxis
                      domain={[0, 5]}
                      ticks={[1, 2, 3, 4, 5]}
                      tick={false}
                      axisLine={false}
                    />
                    <Radar
                      dataKey="full"
                      stroke="#707070"
                      strokeWidth={3}
                      fill="none"
                      dot={false}
                      isAnimationActive={false}
                    />

                    <Radar
                      name="對方"
                      dataKey="partner"
                      fill="#ff7070"
                      fillOpacity={0.5}
                    />

                    <Radar
                      name="你"
                      dataKey="you"
                      fill="#85d6ff"
                      fillOpacity={0.5}
                    />
                  </RadarChart>
                  <div className="compare-grid">
                    <div className="cell header">維度</div>
                    <div className="cell header">
                      <span>你的</span>
                      <span>指數</span>
                    </div>
                    <div className="cell header">
                      <span>{`${objectPronoun}的`}</span>
                      <span>指數</span>
                    </div>
                    <div className="cell header">
                      <span>主力</span>
                    </div>
                    <div className="cell header">解析</div>
                    {scoreTableData.map((item, i) => (
                      <>
                        <div key={i} className="cell dimension">
                          <span>{item.dimension.slice(0, 2)}</span>
                          <span>{item.dimension.slice(2)}</span>
                        </div>
                        <div className="cell">
                          {renderScoreBlocks(item.youScore)}
                        </div>
                        <div className="cell">
                          {renderScoreBlocks(item.partnerScore)}
                        </div>
                        <div className="cell dominant">
                          {item.dominant !== "平手" && (
                            <span className="crown">👑</span>
                          )}
                          <span>{dominantLabel(item.dominant)}</span>
                        </div>
                        <div className="cell analysis">{item.analysis}</div>
                      </>
                    ))}
                  </div>
                  <span className="item-label">總結</span>
                  <div className="item-block">
                    <span className="item-title">{data.summary.simple}</span>
                    <span className="item-content">{data.summary.detail}</span>
                  </div>
                  <div className="memo">
                    <div className="links">
                      <a href={SHARE_URL} onClick={handleShare}>👉 點擊分享 </a>
                      <span>|</span>
                      <a href={LINE_URL} target="_blank" rel="noopener noreferrer">👉 官方LINE客服 </a>
                      <span>|</span>
                      <Link to={`/consult?order=${data.orderID || ""}`}>
                        👉 專業諮詢{" "}
                      </Link>
                    </div>
                    <span className="product-id">{`產品編號 ${data.productID}`}</span>
                    <span className="end">
                      本分析由元辰雲閣命理技術團隊提供，為你在交往前解析關係發展與相處模式。
                    </span>
                  </div>
                </div>
              </div>
              <img
                className="bottom-holder"
                ref={mobileBottomHolderRef}
                src="/images/bg-bottom-reel.png"
                style={{ opacity: 0 }}
              />
            </div>
          ) : (
            <div className="reel-main" ref={mobileReelRef}>
              <img
                className="top-holder"
                ref={mobileTopHolderRef}
                src="/images/bg-top-reel.png"
              />
              <div className="outer-paper" ref={mobileOuterPaperRef}>
                <div className="inner-paper">
                  <div className="status">{`狀態：${StatusMap.loveStatusMap[data.betweenStatus]}`}</div>
                  <div className="between-info">
                    <div className="user">
                      <span>{data.user.name}</span>
                      <span>{`${data.user.gender}性`}</span>
                      <span>{data.user.birth}</span>
                    </div>
                    <img
                      className="love-holder"
                      src="/images/ic-love-report.png"
                    />
                    <div className="user">
                      <span>{data.object.name}</span>
                      <span>{`${data.object.gender}性`}</span>
                      <span>{data.object.birth}</span>
                    </div>
                  </div>
                  <div className="reel-title">
                    <img src="/images/bg-love-top-title.png" />
                    <span className="label">真愛合盤</span>
                    <span className="title">{data.slogan}</span>
                    <img src="/images/bg-love-bottom-title.png" />
                  </div>
                  <span className="item-label">{`個性分析：${data.bwnPersonality.you.label} vs. ${data.bwnPersonality.object.label}`}</span>
                  <div className="item-block">
                    <span className="item-title">{` ● 你：`}</span>
                    <span className="item-content">
                      {data.bwnPersonality.you.des}
                    </span>
                    <span className="item-title">{` ● 對方：`}</span>
                    <span className="item-content">
                      {data.bwnPersonality.object.des}
                    </span>
                  </div>
                  <span className="item-label">溝通模式與互動方式</span>
                  <div className="item-block">
                    <span className="item-title">● 主要溝通模式：</span>
                    <span className="item-content">
                      {data.communicateActive.communicate}
                    </span>
                    <span className="item-title">● 日常互動模式：</span>
                    <span className="item-content">
                      {data.communicateActive.active}
                    </span>
                    <span className="item-title">● 建議：</span>
                    <span className="item-content">
                      {data.communicateActive.advice}
                    </span>
                  </div>
                  <span className="item-label">吵架點分析</span>
                  <div className="item-block">
                    <span className="item-title">{data.quarrel.simple}</span>
                    <span className="item-title">● 你的引爆點：</span>
                    <span className="item-content">
                      {data.quarrel.youOpinion}
                    </span>
                    <span className="item-title">● 對方的引爆點：</span>
                    <span className="item-content">
                      {data.quarrel.objectOpinion}
                    </span>
                    <span className="item-title">● 吵架模式：</span>
                    <span className="item-content">
                      {data.quarrel.situation}
                    </span>
                    <span className="item-title">● 建議：</span>
                    <span className="item-content">{data.quarrel.advice}</span>
                  </div>
                  <span className="item-label">彼此的感情真實需求</span>
                  <div className="demand-grid">
                    <div className="cell header">誰的需求</div>
                    <div className="cell header">感情真實需求</div>
                    <div className="cell role">你</div>
                    <div className="cell content">
                      {data.demandData.youDemand}
                    </div>
                    <div className="cell role">對方</div>
                    <div className="cell content">
                      {data.demandData.objectDemand}
                    </div>
                  </div>
                  <span className="item-label">如何穩定走長期</span>
                  <div className="item-block">
                    <span className="item-title">{`● 長期穩定度評估：${data.stableStrategy.grade}`}</span>
                    {data.stableStrategy.strategys.map((s, i) => (
                      <div className="strateg-block" key={i}>
                        <span className="item-title">{`● ${s.label} :`}</span>
                        <span className="item-content">{s.strategy}</span>
                      </div>
                    ))}
                  </div>
                  <span className="item-label">
                    戀愛合拍指數：<b>{data.matchAttribute.score}</b> 分
                  </span>
                  <div className="match-block">
                    {matchKeys.map(({ title, key }) => (
                      <div className="item-match" key={key}>
                        <div className="match-rating">
                          <span className="match-title">{title}</span>
                          <div className="stars">
                            {Array.from({
                              length: data.matchAttribute[key].stars,
                            }).map((_, i) => (
                              <img key={i} src="/images/ic-yellow-star.png" />
                            ))}
                          </div>
                        </div>
                        <span className="item-content">
                          {data.matchAttribute[key].des}
                        </span>
                      </div>
                    ))}
                  </div>
                  <span className="item-label">戀愛體質雷達圖</span>
                  <RadarChart
                    width={300}
                    height={300}
                    data={chartData}
                    outerRadius="65%"
                    style={{ pointerEvents: "none" }}
                  >
                    <PolarGrid
                      gridType="polygon"
                      stroke="#c6c6c6"
                      strokeDasharray="8 4"
                    />
                    <PolarAngleAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#615557", fontSize: 11, fontWeight: 500 }}
                    />
                    <PolarRadiusAxis
                      domain={[0, 5]}
                      ticks={[1, 2, 3, 4, 5]}
                      tick={false}
                      axisLine={false}
                    />
                    <Radar
                      dataKey="full"
                      stroke="#707070"
                      strokeWidth={3}
                      fill="none"
                      dot={false}
                      isAnimationActive={false}
                    />

                    <Radar
                      name="對方"
                      dataKey="partner"
                      fill="#ff7070"
                      fillOpacity={0.5}
                    />
                    <Radar
                      name="你"
                      dataKey="you"
                      fill="#85d6ff"
                      fillOpacity={0.5}
                    />
                  </RadarChart>
                  <div className="compare-grid">
                    <div className="cell header">維度</div>
                    <div className="cell header">
                      <span>你的</span>
                      <span>指數</span>
                    </div>
                    <div className="cell header">
                      <span>{`${objectPronoun}的`}</span>
                      <span>指數</span>
                    </div>
                    <div className="cell header">
                      <span>主力</span>
                    </div>
                    <div className="cell header">解析</div>
                    {scoreTableData.map((item, i) => (
                      <>
                        <div key={i} className="cell dimension">
                          <span>{item.dimension.slice(0, 2)}</span>
                          <span>{item.dimension.slice(2)}</span>
                        </div>
                        <div className="cell">
                          {renderScoreBlocks(item.youScore)}
                        </div>
                        <div className="cell">
                          {renderScoreBlocks(item.partnerScore)}
                        </div>
                        <div className="cell dominant">
                          {item.dominant !== "平手" && (
                            <span className="crown">👑</span>
                          )}
                          <span>{dominantLabel(item.dominant)}</span>
                        </div>
                        <div className="cell analysis">{item.analysis}</div>
                      </>
                    ))}
                  </div>
                  <span className="item-label">總結</span>
                  <div className="item-block">
                    <span className="item-title">{data.summary.simple}</span>
                    <span className="item-content">{data.summary.detail}</span>
                  </div>
                  <div className="memo">
                    <div className="links">
                      <a href={SHARE_URL} onClick={handleShare}>👉 點擊分享 </a>
                      <span>|</span>
                      <a href={LINE_URL} target="_blank" rel="noopener noreferrer">👉 官方LINE客服 </a>
                      <span>|</span>
                      <Link to={`/consult?order=${data.orderID || ""}`}>
                        👉 專業諮詢{" "}
                      </Link>
                    </div>
                    <span className="product-id">{`產品編號 ${data.productID}`}</span>
                    <span className="end">{`本分析由元辰雲閣命理技術團隊提供，${data.betweenStatus == "0" ? "為你在交往前解析關係發展與相處模式。" : "為你解析交往中的相處模式與需求。"}`}</span>
                  </div>
                </div>
              </div>
              <img
                className="bottom-holder"
                ref={mobileBottomHolderRef}
                src="/images/bg-bottom-reel.png"
                style={{ opacity: 0 }}
              />
            </div>
          )}

          <SiteFooter />
        </div>
      )}
    </div>
  );
};

export default LoveMatchReportComponent;
